// React and Hooks
import { memo, useEffect, useState } from "react";

// Types
import { TollEditProps } from "./types";
import { TollEditFormValues, TollRow } from "services/api/calculations/toll/types";

// API and Services
import { createTollAPI, getTollAPI, getTollsAPI, updateTollAPI } from "services/api/calculations/toll/api";
import { validateYup } from "services/forms/validation/validation";
import { useHandleTextFieldChange } from "services/forms/hooks";
import { initialTollEditForm } from "services/api/calculations/toll/consts";

// Constants
import { CALCULATION_TOLL_EDIT_FORM_SCHEMA } from "./validation";

// UI Components
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";
import { Errors } from "services/forms/validation/types";
import { DataWithPagination, initialPagination } from "ui/components/Table/consts";
import { initialDataWithPagination } from "services/api/consts";

// Helpers
import { useTranslate } from "helpers";


const TollEditCard: React.FC<TollEditProps> = (props) => {
  const { t } = useTranslate();
  const { tollId, onSuccess } = props;
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [vehicleGroups, setTolls] = useState<
    DataWithPagination<TollRow>
  >(initialDataWithPagination);

  const [formValues, setFormValues] = useState<TollEditFormValues>(
    initialTollEditForm
  );

  useEffect(() => {
    if (tollId) {
      fetchToll(tollId);
      fetchTolls();
    }
  }, [tollId]);

  const fetchToll = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialTollEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getTollAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);

    setIsLoading(false);

    setErrors({});
  };

  const fetchTolls = async () => {
    setIsLoading(true);

    let result;

    try {
      result = await getTollsAPI(initialPagination);
    } catch {
      setIsLoading(false);
      return;
    }

    setTolls(result);
    setIsLoading(false);
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      CALCULATION_TOLL_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      let result;
      if (!!tollId) {
        result = await updateTollAPI(tollId, formValues);
      } else {
        result = await createTollAPI(formValues);
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  return (
    <>
      <div className="flex gap-4">
        <div className="w-full m-auto">
          <div className="space-y-3">
            <form noValidate className="text-left">
              <div className="px-5 pb-3 mb-2">
                <div className="flex flex-row gap-3 mb-3">
                  <div className="flex-1 ">
                    <TextField
                      type="text"
                      name="fromCcm"
                      label={t("admin.calculation.setting.toll.add.fromccm")}
                      placeholder={t(
                        "admin.calculation.setting.toll.add.fromccm.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.fromCcm}
                      value={formValues.fromCcm}
                      required
                      endIcon=""
                      groupAppend="ccm"
                    ></TextField>
                  </div>
                  <div className="flex-1 ">
                    <TextField
                      type="text"
                      name="toCcm"
                      label={t("admin.calculation.setting.toll.add.toccm")}
                      placeholder={t(
                        "admin.calculation.setting.toll.add.toccm.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.toCcm}
                      value={formValues.toCcm}
                      required
                      endIcon=""
                      groupAppend="ccm"
                    ></TextField>
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 ">
                    <TextField
                      type="text"
                      name="price"
                      label={t("admin.calculation.setting.toll.add.price")}
                      placeholder={t(
                        "admin.calculation.setting.toll.add.price.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.price}
                      value={formValues.price}
                      required
                      endIcon=""
                      groupAppend="€"
                    ></TextField>
                  </div>
                </div>
              </div>
              <div className="px-5 pb-5 flex justify-end ">
                <Button
                  variant="primary"
                  size="full"
                  children={t("admin.save")}
                  onClick={handleFormSubmit}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(TollEditCard);
